import React, { useEffect, useState, useContext, useMemo } from 'react';
import {
    Card,
    Col,
    Row,
    Button,
    Typography,
    Tag,
    message,
    Layout,
    Select,
    Avatar,
    Statistic,
    Spin,
    Empty,
    Tooltip,
    Modal,
    Table,
    Popconfirm
} from 'antd';
import {
    ReloadOutlined,
    BarChartOutlined,
    UserSwitchOutlined,
    DesktopOutlined,
    UserOutlined,
    FolderOpenOutlined,
    AppstoreOutlined,
    DragOutlined,
    CheckOutlined,
    UndoOutlined,
    ControlOutlined,
    SkinOutlined,
    ThunderboltOutlined,
    HeartFilled,
    GiftOutlined,
    StarFilled,
    ShopOutlined,
    CrownOutlined,
    EyeOutlined,
    AimOutlined,
    FireOutlined,
    SafetyCertificateOutlined,
    CompassOutlined,
    ShoppingOutlined
} from '@ant-design/icons';
import IdeFileBrowserModal from './ide/IdeFileBrowserModal';
import GameSessionModal from './components/GameSessionModal';

import { AuthContext } from "../../config/AuthContext";
import { useNavigate } from "react-router-dom";
import AppSider from "./components/AppSider";
import { GetUsuarisIdJutgeNotes } from "../../dao/GetUsuarisIdJutgeNotes";
import { GetUsuarisIdAulaIdJutgeNotes } from "../../dao/GetUsuarisIdAulesIdJutgeNotes";
import { GetRankingsUsuariId } from "../../dao/jutge/GetRankingsUsuariId";
import { GetAlumneId } from "../../dao/GetAlumneId";
import { DefaultAulumneInfo } from "../../model/AlumneInfo";
import { GetAulaRaConfig } from "../../dao/GetAulaRaConfig";
import { GetUsuariIdJutgeActivitats } from "../../dao/GetUsuariIdJutgeActivitats";
import { GetUsuariIdJutgePractiques } from "../../dao/GetUsuariIdJutgePractiques";
import { GetInteraccionsHistory } from "../../dao/GetInteraccionsHistory";
import ActivitatsIdEstadistiques from "./activitatsId/content/ActivitatsIdEstadistiques";
import PracticaIdAlumnes from "./practiquesId/content/PracticaIdAlumnes";
import { GetClassroomLayout, PostClassroomLayout } from "../../dao/jutge/ClassroomLayoutDao";
import { GetUserEquipment, PostUserEquipment, GetSkins, getJavamonImage } from "../../dao/jutge/GetJavamonConfig";
import { GetJutgeUsuariIdJavamons } from "../../dao/jutge/GetJutgeUsuariIdJavamons";
import { GetJutgeUsuariIdPoints } from "../../dao/jutge/GetJutgeUsuariIdPoints";
import { GetJutgeUsuariIdPointsHistory } from "../../dao/jutge/GetJutgeUsuariIdPointsHistory";
import { GetUserGameStatsUnity, ResetUserGamePosition } from "../../dao/jutge/GetGameSessionsLogs";
import { GetAulaGameSummary } from "../../dao/jutge/GetAulaGameSummary";
import Mochila from "../components/content/Mochila";
import Perfil from "../components/content/Perfil";
import UserInventoryModal from "./javmons/UserInventoryModal";
import UserEquipmentEditModal from "./components/UserEquipmentEditModal";

const { Title, Text } = Typography;
const { Content } = Layout;
const { Option, OptGroup } = Select;


// --- Interfaces ---

interface IdeSession {
    hostname: string;
    port: number;
    ssl: boolean;
    cpu?: string;
    memory?: string;
}

interface SessionsMap {
    [userId: string]: IdeSession;
}

interface StudentNote {
    id_usuari: number;
    id_practica: number;
    nota: number;
}

interface NoteRA {
    id_usuari: number;
    nom_ra: string;
    nota: number;
}

interface StudentData {
    id: number;
    ide?: number | string;
    nom: string;
    cognoms: string;
    avatar?: string;
    // Computed fields
    averageGrade?: number;
    completedTasks?: number;
    totalTasks?: number;
    raGrades?: { nom: string; nota: number | null; isCapped?: boolean }[];
}

interface ClassroomData {
    alumnes: StudentData[];
    practiques: any[];
    notes: StudentNote[];
    notes_ra: NoteRA[];
    ras_aula: any[];
}

// --- Helper Components ---

// Stacked Bar Chart for RAs
const RaStackedBarChart: React.FC<{ students: StudentData[], ras: string[] }> = ({ students, ras }) => {
    // Ranges 0-100 scale. Order: Top to Bottom visually, so we iterate sequentially or use flex-col-reverse
    const ranges = [
        { label: 'Excelente (9-10)', min: 9, max: 11, color: '#52c41a', textColor: '#237804' },
        { label: 'Notable (7-8.9)', min: 7, max: 9, color: '#1890ff', textColor: '#0050b3' },
        { label: 'Bien (6-6.9)', min: 6, max: 7, color: '#faad14', textColor: '#ad6800' },
        { label: 'Aprobado (5-5.9)', min: 5, max: 6, color: '#fa8c16', textColor: '#873800' },
        { label: 'Suspenso (0-4.9)', min: 0, max: 5, color: '#f5222d', textColor: '#a8071a' },
    ];

    if (ras.length === 0) return <Empty description="Sin datos de RA" />;

    return (
        <div style={{ padding: '10px 0', height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Chart Area */}
            <div style={{ flex: 1, display: 'flex', alignItems: 'flex-end', gap: 16, paddingBottom: 24, borderBottom: '1px solid #f0f0f0' }}>
                {ras.map(raName => {
                    // Calculate distribution for this RA
                    const counts = ranges.map(r => ({ ...r, count: 0 }));

                    let validGradesCount = 0;

                    students.forEach(s => {
                        const ra = s.raGrades?.find(r => r.nom === raName);
                        const val = ra && ra.nota !== null ? ra.nota : null;

                        if (val !== null) {
                            const range = counts.find(r => val >= r.min && val < r.max);
                            if (range) {
                                range.count++;
                                validGradesCount++;
                            }
                        }
                    });

                    const total = validGradesCount || 1; // Avoid div by 0 for percentage calcs

                    return (
                        <div key={raName} style={{ flex: 1, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', alignItems: 'center' }}>
                            {/* The Bar */}
                            <div style={{ width: '100%', maxWidth: 40, height: '100%', background: '#f5f5f5', borderRadius: 4, overflow: 'hidden', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
                                {validGradesCount === 0 ? (
                                    <div style={{ height: '4px', background: '#d9d9d9', width: '100%' }} />
                                ) : (
                                    /* Segments - Rendered standard (Top: Excelente, Bottom: Suspenso). Flex direction column */
                                    counts.map((range, idx) => {
                                        const percent = (range.count / total) * 100;
                                        if (percent === 0) return null;
                                        return (
                                            <Tooltip key={idx} title={`${range.label}: ${range.count} alumnos (${percent.toFixed(0)}%)`}>
                                                <div style={{
                                                    height: `${percent}%`,
                                                    background: range.color,
                                                    width: '100%',
                                                    transition: 'height 0.3s'
                                                }} />
                                            </Tooltip>
                                        );
                                    })
                                )}
                            </div>
                            <div style={{ marginTop: 8, fontSize: 12, fontWeight: 'bold' }}>{raName}</div>
                        </div>
                    );
                })}
            </div>

            {/* Legend */}
            <div style={{ display: 'flex', justifyContent: 'center', gap: 16, marginTop: 16, flexWrap: 'wrap' }}>
                {ranges.map(r => (
                    <div key={r.label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <div style={{ width: 10, height: 10, backgroundColor: r.color, borderRadius: 2 }} />
                        <Text style={{ fontSize: 11, color: '#666' }}>{r.label.split(' ')[0]}</Text>
                    </div>
                ))}
            </div>
        </div>
    );
};

// --- Student Detail Modal ---

const StudentDetailModal: React.FC<{
    visible: boolean;
    onClose: () => void;
    student: StudentData | null;
    classroomData: ClassroomData | null;
    raConfig: any[];
}> = ({ visible, onClose, student, classroomData, raConfig }) => {

    if (!student || !classroomData) return null;

    const allRAs = Array.isArray(classroomData.ras_aula) ? classroomData.ras_aula : [];
    // Use calculated grades from student object
    const studentRANotes = new Map((student.raGrades || []).map(r => [r.nom, { nota: r.nota, isCapped: r.isCapped }]));

    const raData = allRAs.map((ra: any) => {
        const raInfo = studentRANotes.get(ra.nom);
        return {
            nom_ra: ra.nom,
            nota: raInfo ? raInfo.nota : null,
            isCapped: raInfo ? raInfo.isCapped : false
        };
    }).sort((a: any, b: any) => a.nom_ra.localeCompare(b.nom_ra));

    const safeNotes = Array.isArray(classroomData.notes) ? classroomData.notes : [];
    const safePractiques = Array.isArray(classroomData.practiques) ? classroomData.practiques : [];

    const mapNotas = new Map(safeNotes.filter(n => n.id_usuari === student.id)
        .map(n => [`${n.id_practica}`, n.nota]));

    // Columns for RA Table
    const raColumns = [
        { title: 'Resultados de Aprendizaje (RA)', dataIndex: 'nom_ra', key: 'nom_ra', render: (text: string) => <b>RA-{text}</b> },
        {
            title: 'Nota Global',
            dataIndex: 'nota',
            key: 'nota',
            align: 'right' as const,
            render: (val: number | null, record: any) => {
                if (val === null) return <Tag>N/P</Tag>;
                const color = val >= 5 ? 'green' : 'red';
                return (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 4 }}>
                        {record.isCapped && (
                            <Tooltip title="Nota limitada a 4 por no alcanzar el mínimo en un instrumento">
                                <span style={{ cursor: 'help', fontSize: 16 }}>⚠️</span>
                            </Tooltip>
                        )}
                        <Tag color={color} style={{ fontSize: 14, padding: '4px 10px' }}>{val.toFixed(2)}</Tag>
                    </div>
                );
            }
        }
    ];

    const renderExpandedRa = (record: any) => {
        // 1. Filter practices for this RA
        const raPractices = safePractiques.filter(p =>
            p.ras && p.ras.some((r: any) => r.nom === record.nom_ra)
        );

        if (raPractices.length === 0) return <Empty description="No hay prácticas asociadas" image={Empty.PRESENTED_IMAGE_SIMPLE} />;

        // 2. Group practices by instrument
        const instrumentMap: Record<string, any[]> = {};
        raPractices.forEach(p => {
            const raObj = p.ras?.find((r: any) => r.nom === record.nom_ra);
            const inst = raObj?.instrument || p.inst || 'Sin Instrumento';
            if (!instrumentMap[inst]) instrumentMap[inst] = [];
            instrumentMap[inst].push(p);
        });

        // 3. Calculate instrument-level data
        const instrumentData = Object.keys(instrumentMap).map(inst => {
            const practices = instrumentMap[inst];

            // Get configuration for this instrument
            const configItems = raConfig.filter((c: any) => c.nom_ra === record.nom_ra);
            const conf = configItems.find((c: any) => c.instrument === inst);
            const totalWeight = conf ? conf.pes : 0;

            // Calculate average grade for this instrument
            const grades = practices.map(p => mapNotas.get(`${p.id}`)).filter(g => g !== null && g !== undefined) as number[];
            const avgGrade = grades.length > 0 ? (grades.reduce((a: number, b: number) => a + b, 0) / grades.length) / 10 : null;

            return {
                key: inst,
                instrument: inst,
                weight: totalWeight,
                avgGrade: avgGrade,
                practices: practices.map(p => ({
                    key: p.id,
                    nom: p.nom,
                    inst: inst,
                    nota: mapNotas.get(`${p.id}`) ?? null,
                    weight: totalWeight,
                    effectiveWeight: practices.length > 0 ? (totalWeight / practices.length) : 0
                }))
            };
        });

        // 4. Columns for instruments table
        const instrumentColumns = [
            {
                title: 'Instrumento',
                dataIndex: 'instrument',
                key: 'instrument',
                render: (inst: string) => <b>{inst}</b>
            },
            {
                title: 'Peso',
                dataIndex: 'weight',
                key: 'weight',
                align: 'center' as const,
                render: (w: number) => <Tag color="blue">{w.toFixed(2)}%</Tag>
            },
            {
                title: 'Nota Final',
                dataIndex: 'avgGrade',
                key: 'avgGrade',
                align: 'right' as const,
                render: (val: number | null) => val !== null ? (
                    <Tag color={val >= 50 ? 'green' : 'red'}>{val.toFixed(2)}</Tag>
                ) : <Tag>N/P</Tag>
            }
        ];

        // 5. Columns for practices table (when expanding instrument)
        const practiceColumns = [
            { title: 'Práctica / Actividad', dataIndex: 'nom', key: 'nom' },
            {
                title: 'Contribución Real',
                dataIndex: 'effectiveWeight',
                key: 'effectiveWeight',
                align: 'center' as const,
                render: (v: number) => <Tag color="cyan">{v.toFixed(2)}%</Tag>
            },
            {
                title: 'Nota',
                dataIndex: 'nota',
                key: 'nota',
                align: 'right' as const,
                render: (val: number | null) => val !== null ? (
                    <Tag color={val >= 50 ? 'green' : 'red'}>{val.toFixed(2)}</Tag>
                ) : <Tag>N/P</Tag>
            }
        ];

        return (
            <Table
                columns={instrumentColumns}
                dataSource={instrumentData}
                pagination={false}
                size="small"
                bordered
                expandable={{
                    expandedRowRender: (instrumentRecord) => (
                        <Table
                            columns={practiceColumns}
                            dataSource={instrumentRecord.practices}
                            pagination={false}
                            size="small"
                            showHeader={true}
                        />
                    ),
                    rowExpandable: (instrumentRecord) => instrumentRecord.practices && instrumentRecord.practices.length > 0
                }}
            />
        );
    };

    return (
        <Modal
            title={<div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <Avatar src={student.avatar} size="large">{student.nom[0]}</Avatar>
                <div>
                    <div style={{ fontWeight: 'bold' }}>{student.nom} {student.cognoms}</div>
                    <div style={{ fontSize: 12, fontWeight: 'normal', color: '#666' }}>Detalle de Calificaciones (Sobre 10)</div>
                </div>
            </div>}
            open={visible}
            onCancel={onClose}
            footer={[<Button key="close" onClick={onClose}>Cerrar</Button>]}
            width={900}
        >
            <Table
                columns={raColumns}
                dataSource={raData}
                rowKey="nom_ra"
                pagination={false}
                expandable={{
                    expandedRowRender: renderExpandedRa,
                    expandRowByClick: true
                }}
            />
        </Modal>
    );
};


// --- Dashboard Component ---

const Dashboard = () => {
    const navigate = useNavigate();
    const { userId, setLoggedIn, setUserId, setUser, setAlumneInfo, setUserPermisos, setBreadCrumb, user, alumne } = useContext(AuthContext);

    const [sessions, setSessions] = useState<SessionsMap>({});
    const [aulas, setAulas] = useState<{ id: number; nom: string }[]>([]);
    const [selectedAula, setSelectedAula] = useState<number | null>(null);
    const [classroomData, setClassroomData] = useState<ClassroomData | null>(null);
    const [loadingData, setLoadingData] = useState(false);
    const [avatars, setAvatars] = useState<Record<string, string>>({});
    const [selectedStudent, setSelectedStudent] = useState<StudentData | null>(null);
    const [modalVisible, setModalVisible] = useState(false);
    const [selectedGameStudent, setSelectedGameStudent] = useState<StudentData | null>(null);
    const [gameSessionModalVisible, setGameSessionModalVisible] = useState(false);
    const [raConfig, setRaConfig] = useState<any[]>([]);
    const [allActivitats, setAllActivitats] = useState<any[]>([]);
    const [allPractiques, setAllPractiques] = useState<any[]>([]);
    const [selectedItemKey, setSelectedItemKey] = useState<string | null>(null); // e.g. "act-12" or "prac-5"

    // Classroom layout mode ('aula' | 'graella' | 'joc') and positions: { [key: string]: { x: number, y: number } }
    const [viewMode, setViewMode] = useState<'aula' | 'graella' | 'joc'>('aula');
    const [cardPositions, setCardPositions] = useState<Record<string, { x: number; y: number }>>({});
    const [savingLayout, setSavingLayout] = useState(false);

    // Game data for Vista Joc
    const [catalogSkins, setCatalogSkins] = useState<any>(null);
    const [studentsGameData, setStudentsGameData] = useState<Record<number, {
        equipment?: any;
        stats?: any;
        pointsTotal?: number;
        pointsCurrent?: number;
        javamons?: any[];
        objects?: any[];
    }>>({});
    const [loadingGameData, setLoadingGameData] = useState(false);

    // Modals for Vista Joc: RPG Profile, Gestió d'Inventari, Equipament
    const [rpgProfileModal, setRpgProfileModal] = useState<{ visible: boolean; student: StudentData | null }>({ visible: false, student: null });
    const [userInventoryModal, setUserInventoryModal] = useState<{ visible: boolean; student: StudentData | null }>({ visible: false, student: null });
    const [userEquipmentModal, setUserEquipmentModal] = useState<{ visible: boolean; student: any | null; editingUser: any | null }>({ visible: false, student: null, editingUser: null });
    const [loadingEquipmentUser, setLoadingEquipmentUser] = useState<number | null>(null);

    useEffect(() => {
        // Si el usuario es alumno y alguna de sus aulas está en modo examen, la pantalla principal de jutge es Exàmens
        if (user?.tipus === "2") {
            const userAulas = alumne?.aules || [];
            const isExamMode = (userAulas || []).some((a: any) =>
                Boolean(a.isExamen === 1 || a.isExamen === true || a.is_examen === 1 || a.is_examen === true)
            );
            if (isExamMode) {
                navigate('/jutge/examens');
                return;
            }
        }

        setBreadCrumb([{ title: "Jutge" }, { title: "Dashboard" }], true);
        fetchSessions();
        fetchClassrooms();
        fetchAvatars();
        fetchUserActivitatsAndPractiques();

        const interval = setInterval(fetchSessions, 30000);
        return () => clearInterval(interval);
    }, []);

    const fetchUserActivitatsAndPractiques = async () => {
        try {
            const [acts, pracs] = await Promise.all([
                GetUsuariIdJutgeActivitats(userId),
                GetUsuariIdJutgePractiques(userId)
            ]);
            setAllActivitats(acts || []);
            setAllPractiques(pracs || []);
        } catch (e) {
            console.error("Error cargando actividades y prácticas", e);
        }
    };

    useEffect(() => {
        if (selectedAula) {
            setSelectedItemKey(null); // reset selected item on classroom change
            fetchClassroomStats(selectedAula);
        } else {
            setClassroomData(null);
            setSelectedItemKey(null);
            setCardPositions({});
        }
    }, [selectedAula]);



    // --- API Calls ---

    const fetchSessions = async () => {
        try {
            const token = localStorage.getItem('authToken');
            const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost';

            const response = await fetch(`${baseUrl}:8061/jutge/ide/admin/sessions`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                const data = await response.json();
                setSessions(data);
            }
        } catch (error) {
            console.error("Failed to fetch sessions", error);
        }
    };

    const fetchClassrooms = async () => {
        try {
            const data = await GetUsuarisIdJutgeNotes(userId);
            const list: any[] = Array.isArray(data) ? data : [];
            const uniqueAulasMap = new Map();
            list.flatMap(student => student.practiques || []).forEach((p: any) => {
                if (p.id_aula && p.aula) {
                    uniqueAulasMap.set(p.id_aula, p.aula);
                }
            });
            const aulasList = Array.from(uniqueAulasMap.entries()).map(([id, nom]) => ({ id, nom }));
            setAulas(aulasList);
            if (aulasList.length > 0) {
                setSelectedAula(aulasList[0].id);
            }
        } catch (error) {
            console.error(error);
            message.error("Error cargando aulas");
        }
    };

    const fetchClassroomStats = async (aulaId: number) => {
        setLoadingData(true);
        try {
            const [res, config, layout] = await Promise.all([
                GetUsuarisIdAulaIdJutgeNotes(userId + "", aulaId + ""),
                GetAulaRaConfig(aulaId + ""),
                GetClassroomLayout(aulaId)
            ]);
            setClassroomData(res);
            setRaConfig(config || []);
            if (layout && typeof layout === 'object' && Object.keys(layout).length > 0) {
                setCardPositions(layout);
            } else {
                setCardPositions({});
            }
        } catch (error) {
            console.error(error);
            message.error("Error cargando datos del aula");
        } finally {
            setLoadingData(false);
        }
    };

    const handleSaveLayout = async (positionsToSave?: Record<string, { x: number; y: number }>) => {
        if (!selectedAula) return;
        const toSave = positionsToSave || cardPositions;
        setSavingLayout(true);
        try {
            const success = await PostClassroomLayout(selectedAula, toSave);
            if (success) {
                message.success("Disposición del aula guardada correctamente");
            } else {
                message.error("Error al guardar la disposición");
            }
        } catch (e) {
            console.error("Error guardando layout aula:", e);
            message.error("Error al guardar la disposición");
        } finally {
            setSavingLayout(false);
        }
    };

    const handleResetLayout = () => {
        setCardPositions({});
        if (selectedAula) {
            PostClassroomLayout(selectedAula, {});
            message.info("Disposición restablecida");
        }
    };

    const fetchAvatars = async () => {
        try {
            const data = await GetRankingsUsuariId(userId + "");
            if (data) {
                const rankingData = Array.isArray(data) ? data : data.ranking || [];
                const map: Record<string, string> = {};
                rankingData.forEach((r: any) => {
                    const uid = r.id_usuari || r.id;
                    if (uid && r.avatar) {
                        map[String(uid)] = r.avatar;
                    }
                });
                setAvatars(map);
            }
        } catch (e) {
            console.error("Error fetching avatars", e);
        }
    };


    const fetchClassroomGameData = async () => {
        if (!selectedAula || !classroomData || !classroomData.alumnes || classroomData.alumnes.length === 0) return;
        setLoadingGameData(true);
        try {
            // Load catalog skins once if not loaded
            if (!catalogSkins) {
                const skinsRes = await GetSkins();
                if (skinsRes) {
                    const formattedSkins = {
                        skins: skinsRes.skins ? skinsRes.skins.map((x: any) => ({ ...x, _type: 'skin' })) : [],
                        armes: skinsRes.armes ? skinsRes.armes.map((x: any) => ({ ...x, _type: 'arma' })) : [],
                        escuts: skinsRes.escuts ? skinsRes.escuts.map((x: any) => ({ ...x, _type: 'escut' })) : [],
                        arcs: skinsRes.arcs ? skinsRes.arcs.map((x: any) => ({ ...x, _type: 'arc' })) : [],
                        flechas: skinsRes.flechas ? skinsRes.flechas.map((x: any) => ({ ...x, _type: 'flecha' })) : [],
                        magias: skinsRes.magias ? skinsRes.magias.map((x: any) => ({ ...x, _type: 'magia' })) : [],
                        avatars: skinsRes.avatars ? skinsRes.avatars.map((x: any) => ({ ...x, _type: 'avatar' })) : []
                    };
                    setCatalogSkins(formattedSkins);
                }
            }

            // Single compact call for all students in the classroom
            const summary = await GetAulaGameSummary(selectedAula);
            let hasValidSummary = summary && summary.length > 0;

            if (hasValidSummary) {
                const dataMap: Record<number, any> = {};
                summary.forEach((item: any) => {
                    dataMap[item.id] = {
                        equipment: item.equipment,
                        stats: item.stats,
                        pointsTotal: item.pointsTotal ?? 0,
                        pointsCurrent: item.pointsCurrent ?? 0,
                        javamons: item.javamons || [],
                        objects: item.objects || []
                    };
                });

                // If points are 0 in summary, populate points from individual points API in parallel
                const needsPoints = summary.some((item: any) => !item.pointsTotal && !item.pointsCurrent);
                if (needsPoints) {
                    await Promise.all(classroomData.alumnes.map(async (student) => {
                        const sId = student.id;
                        try {
                            const [ptsData, historyPts] = await Promise.all([
                                GetJutgeUsuariIdPoints(sId + "").catch(() => []),
                                GetJutgeUsuariIdPointsHistory(sId + "").catch(() => [])
                            ]);
                            const sourceEntries = Array.isArray(historyPts) && historyPts.length > 0 ? historyPts : (Array.isArray(ptsData) ? ptsData : []);
                            const userPointsHistory = sourceEntries.filter((p: any) => p.id_item == null && p.ball !== 'JAVABALL_LOG' && Number(p.punts) > 0);
                            const userSpentPointsHistory = sourceEntries.filter((p: any) => {
                                const val = Number(p.punts) || 0;
                                const tipo = (p.tipo || '').toLowerCase();
                                const desc = (p.descripcio || '').toLowerCase();
                                return val < 0 || p.ball === 'JAVABALL_LOG' || tipo.includes('gasto') || tipo.includes('lanzamiento') || desc.includes('compra') || desc.includes('penalitzaci');
                            });
                            const ptsTotal = userPointsHistory.reduce((acc, curr) => acc + (Number(curr.punts) || 0), 0);
                            const totalGasto = userSpentPointsHistory
                                .filter((p: any) => p.ball !== 'JAVABALL_LOG')
                                .reduce((acc, curr) => acc + Math.abs(Number(curr.punts) || 0), 0);
                            const ptsCurrent = ptsTotal - totalGasto;

                            if (dataMap[sId]) {
                                dataMap[sId].pointsTotal = ptsTotal;
                                dataMap[sId].pointsCurrent = ptsCurrent;
                            }
                        } catch (e) {
                            // Keep existing summary points
                        }
                    }));
                }

                setStudentsGameData(prev => ({ ...prev, ...dataMap }));
            } else {
                // Fallback fetching equipment, stats, and real points per student
                const promises = classroomData.alumnes.map(async (student) => {
                    const sId = student.id;
                    try {
                        const [eqRes, statsRes, ptsData, historyPts] = await Promise.all([
                            GetUserEquipment(sId),
                            GetUserGameStatsUnity(sId),
                            GetJutgeUsuariIdPoints(sId + "").catch(() => []),
                            GetJutgeUsuariIdPointsHistory(sId + "").catch(() => [])
                        ]);

                        const sourceEntries = Array.isArray(historyPts) && historyPts.length > 0 ? historyPts : (Array.isArray(ptsData) ? ptsData : []);
                        const userPointsHistory = sourceEntries.filter((p: any) => p.id_item == null && p.ball !== 'JAVABALL_LOG' && Number(p.punts) > 0);
                        const userSpentPointsHistory = sourceEntries.filter((p: any) => {
                            const val = Number(p.punts) || 0;
                            const tipo = (p.tipo || '').toLowerCase();
                            const desc = (p.descripcio || '').toLowerCase();
                            return val < 0 || p.ball === 'JAVABALL_LOG' || tipo.includes('gasto') || tipo.includes('lanzamiento') || desc.includes('compra') || desc.includes('penalitzaci');
                        });
                        const ptsTotal = userPointsHistory.reduce((acc, curr) => acc + (Number(curr.punts) || 0), 0);
                        const totalGasto = userSpentPointsHistory
                            .filter((p: any) => p.ball !== 'JAVABALL_LOG')
                            .reduce((acc, curr) => acc + Math.abs(Number(curr.punts) || 0), 0);
                        const ptsCurrent = ptsTotal - totalGasto;

                        return {
                            id: sId,
                            data: {
                                equipment: eqRes,
                                stats: statsRes,
                                pointsTotal: ptsTotal,
                                pointsCurrent: ptsCurrent,
                                javamons: [],
                                objects: []
                            }
                        };
                    } catch {
                        return {
                            id: sId,
                            data: { equipment: null, stats: null, pointsTotal: 0, pointsCurrent: 0, javamons: [], objects: [] }
                        };
                    }
                });
                const results = await Promise.all(promises);
                const dataMap: Record<number, any> = {};
                results.forEach(r => { dataMap[r.id] = r.data; });
                setStudentsGameData(prev => ({ ...prev, ...dataMap }));
            }
        } catch (e) {
            console.error("Error fetching classroom game data", e);
        } finally {
            setLoadingGameData(false);
        }
    };

    const handleResetPosition = async (studentId: number | string, studentName?: string) => {
        try {
            message.loading({ content: `Restableciendo posición a (0,0)...`, key: `reset-pos-${studentId}` });
            const success = await ResetUserGamePosition(studentId);
            if (success) {
                message.success({ content: `Posición de ${studentName || 'alumno'} restablecida a (0,0)`, key: `reset-pos-${studentId}` });
            } else {
                message.error({ content: `Error al restablecer la posición`, key: `reset-pos-${studentId}` });
            }
        } catch (e) {
            console.error(e);
            message.error({ content: `Error de conexión al restablecer posición`, key: `reset-pos-${studentId}` });
        }
    };

    const handleOpenUserEquipment = async (student: any) => {
        try {
            setLoadingEquipmentUser(student.id);
            // Ensure skins config is loaded
            let currentSkins = catalogSkins;
            if (!currentSkins) {
                const skinsRes = await GetSkins();
                if (skinsRes) {
                    currentSkins = {
                        skins: skinsRes.skins ? skinsRes.skins.map((x: any) => ({ ...x, _type: 'skin' })) : [],
                        armes: skinsRes.armes ? skinsRes.armes.map((x: any) => ({ ...x, _type: 'arma' })) : [],
                        escuts: skinsRes.escuts ? skinsRes.escuts.map((x: any) => ({ ...x, _type: 'escut' })) : [],
                        arcs: skinsRes.arcs ? skinsRes.arcs.map((x: any) => ({ ...x, _type: 'arc' })) : [],
                        flechas: skinsRes.flechas ? skinsRes.flechas.map((x: any) => ({ ...x, _type: 'flecha' })) : [],
                        magias: skinsRes.magias ? skinsRes.magias.map((x: any) => ({ ...x, _type: 'magia' })) : [],
                        avatars: skinsRes.avatars ? skinsRes.avatars.map((x: any) => ({ ...x, _type: 'avatar' })) : []
                    };
                    setCatalogSkins(currentSkins);
                }
            }

            // Fetch latest equipment for this specific user to ensure we have full unlocked & equipped fields
            const eqData = await GetUserEquipment(student.id);
            const editingData = {
                ...student,
                id_usuari: student.id,
                ...(eqData || studentsGameData[student.id]?.equipment || {})
            };

            setUserEquipmentModal({
                visible: true,
                student,
                editingUser: editingData
            });
        } catch (e) {
            console.error("Error al abrir equipamiento:", e);
            message.error("Error al cargar equipamiento del alumno");
        } finally {
            setLoadingEquipmentUser(null);
        }
    };

    const handleSaveUserEquipment = async (values: any) => {
        try {
            const unlocked = values.unlocked_avatars || [];
            if (!unlocked.includes(1)) unlocked.push(1);
            const unlockedSkins = values.unlocked_skins || [];
            if (!unlockedSkins.includes(1)) unlockedSkins.push(1);
            const unlockedArmes = values.unlocked_armes || [];
            if (!unlockedArmes.includes(1)) unlockedArmes.push(1);
            const unlockedEscuts = values.unlocked_escuts || [];
            if (!unlockedEscuts.includes(1)) unlockedEscuts.push(1);
            const unlockedArcs = values.unlocked_arcs || [];
            if (!unlockedArcs.includes(1)) unlockedArcs.push(1);
            const unlockedFlechas = values.unlocked_flechas || [];
            if (!unlockedFlechas.includes(1)) unlockedFlechas.push(1);
            const unlockedMagias = values.unlocked_magias || [];
            if (!unlockedMagias.includes(1)) unlockedMagias.push(1);

            const sId = userEquipmentModal.editingUser?.id_usuari || userEquipmentModal.student?.id;
            const body = {
                id_usuari: sId,
                id_skin: values.id_skin || null,
                id_arma: values.id_arma || null,
                id_escut: values.id_escut || null,
                id_arc: values.id_arc || null,
                id_flecha: values.id_flecha || null,
                id_magia: values.id_magia || null,
                id_avatar: values.id_avatar || null,
                unlocked_avatars: unlocked,
                unlocked_skins: unlockedSkins,
                unlocked_armes: unlockedArmes,
                unlocked_escuts: unlockedEscuts,
                unlocked_arcs: unlockedArcs,
                unlocked_flechas: unlockedFlechas,
                unlocked_magias: unlockedMagias
            };

            const res = await PostUserEquipment(body);
            if (res) {
                message.success("Equipamiento guardado correctamente");
                setUserEquipmentModal({ visible: false, student: null, editingUser: null });

                // Refresh equipment in studentsGameData
                if (sId) {
                    const updatedEq = await GetUserEquipment(sId);
                    if (updatedEq) {
                        setStudentsGameData(prev => ({
                            ...prev,
                            [sId]: {
                                ...prev[sId],
                                equipment: updatedEq
                            }
                        }));
                    }
                }
            } else {
                throw new Error("Error en api");
            }
        } catch (e) {
            console.error("Error al guardar equipamiento:", e);
            message.error("Error al guardar equipamiento");
        }
    };

    useEffect(() => {
        if (viewMode === 'joc' && classroomData && classroomData.alumnes) {
            fetchClassroomGameData();
        }
    }, [viewMode, selectedAula, classroomData?.alumnes?.length]);

    // --- Actions ---

    const handleImpersonate = async (targetId: string) => {
        try {
            const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost';
            message.loading("Impersonando usuario...");

            const response = await fetch(`${baseUrl}:8061/login/impersonate`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                },
                body: JSON.stringify({ adminId: userId, targetId })
            });

            if (response.ok) {
                const data = await response.json();
                const { token, permisos } = data;

                let alu = DefaultAulumneInfo;
                if (permisos && permisos.length > 0) {
                    const res = await GetAlumneId(permisos[0].id, token);
                    if (res) alu = res;
                }

                if (!localStorage.getItem('adminToken')) {
                    localStorage.setItem('adminToken', localStorage.getItem('authToken') || '');
                    localStorage.setItem('admin_userId', localStorage.getItem('userId') || '');
                    localStorage.setItem('admin_user', localStorage.getItem('user') || '');
                    localStorage.setItem('admin_userPermisos', localStorage.getItem('userPermisos') || '');
                    localStorage.setItem('admin_alumneInfo', localStorage.getItem('alumneInfo') || '');
                }

                localStorage.setItem('authToken', token);

                setLoggedIn(true);
                setUserId(String(permisos[0].id));
                setUser(permisos[0]);
                setUserPermisos(permisos);
                setAlumneInfo(alu);

                message.success('Impersonando...');
                window.location.reload();

            } else {
                message.error('Error al impersonar');
            }
        } catch (e) {
            console.error(e);
            message.error('Error de conexión');
        }
    };

    const handleStudentClick = (student: StudentData) => {
        setSelectedStudent(student);
        setModalVisible(true);
    };


    // --- Computed Data ---

    const processedStudents = useMemo(() => {
        if (!classroomData || !Array.isArray(classroomData.alumnes)) return [];

        const safeNotes = Array.isArray(classroomData.notes) ? classroomData.notes : [];
        const safePractiques = Array.isArray(classroomData.practiques) ? classroomData.practiques : [];
        const safeRasAula = Array.isArray(classroomData.ras_aula) ? classroomData.ras_aula : [];
        const safeNotesRa = Array.isArray(classroomData.notes_ra) ? classroomData.notes_ra : [];
        const safeRaConfig = Array.isArray(raConfig) ? raConfig : [];

        const mapNotas = new Map(safeNotes.map((n: any) => [`${n.id_usuari}-${n.id_practica}`, n.nota]));

        return classroomData.alumnes.map(student => {
            const raGrades = safeRasAula.map((ra: any) => {
                const raName = ra.nom;
                const configItems = safeRaConfig.filter((c: any) => c.nom_ra === raName);

                let raTotal: number | null = 0;
                let calculated = false;
                let failedInstrument = false;
                let isCapped = false;

                if (configItems.length > 0) {
                    const totalWeight = configItems.filter(c => !c.isRecovery).reduce((sum, c) => sum + (c.pes || 0), 0);

                    if (totalWeight === 0) {
                        const dbVal = safeNotesRa.find(n => n.id_usuari === student.id && n.nom_ra === raName);
                        if (dbVal !== undefined) {
                            return { nom: raName, nota: dbVal.nota, isCapped: false };
                        } else {
                            const raPractices = safePractiques.filter((p: any) =>
                                p.ras?.some((r: any) => r.nom === raName)
                            );
                            let sum = 0;
                            let count = 0;
                            raPractices.forEach((p: any) => {
                                const note = mapNotas.get(`${student.id}-${p.id}`);
                                if (note !== null && note !== undefined) {
                                    sum += Number(note);
                                    count++;
                                }
                            });
                            const avg = count > 0 ? (sum / count) / 10 : null;
                            return { nom: raName, nota: avg, isCapped: false };
                        }
                    }

                    calculated = true;
                    const instGrades: Record<string, number | null> = {};

                    configItems.forEach((conf: any) => {
                        const inst = conf.instrument;
                        const practices = safePractiques.filter((p: any) =>
                            p.ras?.some((r: any) => r.nom === raName && r.instrument === inst)
                        );

                        let instAvg: number | null = null;
                        if (practices.length > 0) {
                            const grades = practices.map((p: any) => mapNotas.get(`${student.id}-${p.id}`)).filter((n: any) => n !== null && n !== undefined);
                            if (grades.length > 0) {
                                instAvg = (grades.reduce((acc: number, n: number) => acc + n, 0) / grades.length) / 10;
                            }
                        }
                        instGrades[inst] = instAvg;
                    });

                    const modifiedGrades = { ...instGrades };
                    let finalGradeOverride: number | null = null;
                    let finalGradeCapped = false;

                    configItems.forEach((item: any) => {
                        if (!item.isRecovery) return;

                        let recGrade = instGrades[item.instrument];
                        if (recGrade === null || recGrade === undefined || recGrade <= 0) return;

                        if (recGrade > 10) recGrade /= 10;

                        if (item.recoveryType === 'final_grade') {
                            const rawCap = item.notaMaxima;
                            const cap = (rawCap !== null && rawCap !== undefined && rawCap !== '') ? parseFloat(rawCap) : null;
                            let computedGrade = recGrade;

                            if (cap !== null && !isNaN(cap) && recGrade > cap) {
                                computedGrade = cap;
                            }

                            if (finalGradeOverride !== null && finalGradeOverride >= 5 && computedGrade < finalGradeOverride) {
                                // Mantener la nota si ya estaba aprobado con una recuperación anterior
                            } else {
                                finalGradeOverride = computedGrade;
                                finalGradeCapped = finalGradeOverride < (item.minim || 0);
                            }
                        } else {
                            const target = item.targetInstrument || 'Prueba Practica';
                            const currentGrade = modifiedGrades[target] ?? 0;

                            if (item.recoveryType === 'max') {
                                modifiedGrades[target] = Math.max(currentGrade, recGrade);
                            } else if (item.recoveryType === 'average') {
                                modifiedGrades[target] = (currentGrade + recGrade) / 2;
                            } else if (item.recoveryType === 'replace') {
                                modifiedGrades[target] = recGrade;
                            }
                        }
                    });

                    raTotal = 0;
                    configItems.forEach((conf: any) => {
                        if (conf.isRecovery) return;

                        const grade = modifiedGrades[conf.instrument];

                        if (grade !== null && grade !== undefined) {
                            const min = conf.minim || 0;
                            if (grade < min) {
                                failedInstrument = true;
                            }
                            raTotal! += (grade * ((conf.pes || 0) / 100));
                            calculated = true;
                        }
                    });

                    if (finalGradeOverride !== null && finalGradeOverride > raTotal!) {
                        raTotal = finalGradeOverride;
                        failedInstrument = finalGradeCapped;
                        calculated = true;
                    }

                    if (!calculated && finalGradeOverride === null) {
                        const raPractices = safePractiques.filter((p: any) =>
                            p.ras?.some((r: any) => r.nom === raName)
                        );
                        let hasAnySubmission = false;
                        for (const p of raPractices) {
                            const note = mapNotas.get(`${student.id}-${p.id}`);
                            if (note !== null && note !== undefined) {
                                hasAnySubmission = true;
                                break;
                            }
                        }
                        if (hasAnySubmission) {
                            calculated = true;
                        }
                    }
                } else {
                    const dbNote = safeNotesRa.find(n => n.id_usuari === student.id && n.nom_ra === raName);
                    if (dbNote) {
                        raTotal = dbNote.nota;
                        calculated = true;
                    } else {
                        const raPractices = safePractiques.filter((p: any) => p.ras?.some((r: any) => r.nom === raName));
                        const grades = raPractices.map((p: any) => mapNotas.get(`${student.id}-${p.id}`)).filter((n: any) => n !== null && n !== undefined);
                        if (grades.length > 0) {
                            raTotal = (grades.reduce((acc: number, n: number) => acc + n, 0) / grades.length) / 10;
                            calculated = true;
                        }
                    }
                }

                if (calculated && failedInstrument) {
                    if (raTotal! > 4) {
                        raTotal = 4;
                        isCapped = true;
                    }
                }

                return { nom: raName, nota: calculated ? raTotal : null, isCapped };
            }).sort((a: any, b: any) => a.nom.localeCompare(b.nom));

            // Compute Average (0-10 scale)
            const validRaGrades = raGrades.filter((r: any) => r.nota !== null);
            let avg = 0;
            if (validRaGrades.length > 0) {
                avg = validRaGrades.reduce((acc, r) => acc + (r.nota || 0), 0) / validRaGrades.length;
            } else {
                let totalScore = 0;
                let count = 0;
                safePractiques.forEach(p => {
                    const note = mapNotas.get(`${student.id}-${p.id}`);
                    if (note !== undefined && note !== null) {
                        totalScore += Number(note);
                        count++;
                    }
                });
                avg = count > 0 ? (totalScore / count) / 10 : 0;
            }

            return {
                ...student,
                averageGrade: avg,
                totalTasks: safePractiques.length,
                raGrades: raGrades,
                avatar: getAvatarSrc(student.avatar || avatars[student.id])
            };
        }).sort((a, b) => (b.averageGrade || 0) - (a.averageGrade || 0));
    }, [classroomData, avatars, raConfig]);

    function getAvatarSrc(img: string | undefined | null) {
        if (!img || img === "undefined" || img === "null") return undefined;
        if (img.startsWith("http") || img.startsWith("data:") || img.startsWith("/")) return img;
        if (img.startsWith("base64:")) img = img.split(":").pop()!;
        if (img.startsWith("/9j/")) return `data:image/jpeg;base64,${img}`;
        if (img.startsWith("iVB")) return `data:image/png;base64,${img}`;
        return `data:image/png;base64,${img}`;
    }

    const globalStats = useMemo(() => {
        if (processedStudents.length === 0 || !classroomData) return { avg: 0, active: 0, passRate: 0, raAverages: [] };

        const totalAvg = processedStudents.reduce((acc, s) => acc + (s.averageGrade || 0), 0) / processedStudents.length;
        const activeCount = processedStudents.filter(s => sessions[s.id + ""]).length;
        const passCount = processedStudents.filter(s => (s.averageGrade || 0) >= 5).length;

        const safeRasAula = Array.isArray(classroomData.ras_aula) ? classroomData.ras_aula : [];

        const raAverages = safeRasAula.map((ra: any) => {
            const grades = processedStudents.map(student => {
                const raNote = student.raGrades?.find((r: any) => r.nom === ra.nom);
                return raNote ? raNote.nota : null;
            }).filter(n => n !== null) as number[];

            const avg = grades.length > 0 ? grades.reduce((a, b) => a + b, 0) / grades.length : 0;
            return { nom: ra.nom, avg };
        }).sort((a: any, b: any) => a.nom.localeCompare(b.nom));

        return {
            avg: totalAvg,
            active: activeCount,
            passRate: (passCount / processedStudents.length) * 100,
            raAverages
        };
    }, [processedStudents, sessions, classroomData]);

    const stringToColor = (str: string) => {
        let hash = 0;
        for (let i = 0; i < str.length; i++) {
            hash = str.charCodeAt(i) + ((hash << 5) - hash);
        }
        const c = (hash & 0x00FFFFFF).toString(16).toUpperCase();
        return '#' + '00000'.substring(0, 6 - c.length) + c;
    };

    // Color scale for 0-100
    const getGradeColor = (grade: number) => {
        if (grade >= 9) return '#52c41a'; // Excelente
        if (grade >= 7) return '#1890ff'; // Notable
        if (grade >= 6) return '#faad14'; // Bien
        if (grade >= 5) return '#fa8c16'; // Aprobado
        return '#f5222d'; // Suspenso
    };

    const [browserUser, setBrowserUser] = useState<{ id: string; name: string } | null>(null);
    const [browserVisible, setBrowserVisible] = useState(false);

    const openFileBrowser = (student: StudentData) => {
        setBrowserUser({ id: student.id + '', name: `${student.nom} ${student.cognoms}` });
        setBrowserVisible(true);
    };


    // --- Student Dashboard Logic ---
    const isStudent = user && user.tipus !== "0" && user.tipus !== "1";
    const [studentActivities, setStudentActivities] = useState<any[]>([]);
    const [studentPractices, setStudentPractices] = useState<any[]>([]);
    const [activeChats, setActiveChats] = useState(0);
    const [closedChats, setClosedChats] = useState(0);
    const [participation, setParticipation] = useState(0);

    useEffect(() => {
        if (isStudent && userId) {
            fetchStudentData();
        }
    }, [isStudent, userId]);

    const fetchStudentData = async () => {
        setLoadingData(true);
        try {
            // Load Activities & Practices directly for correct grades/order
            const [acts, pracs, interactions] = await Promise.all([
                GetUsuariIdJutgeActivitats(userId),
                GetUsuariIdJutgePractiques(userId),
                GetInteraccionsHistory(userId)
            ]);

            const userAulas = alumne?.aules || [];
            const isExamMode = (userAulas || []).some((a: any) =>
                Boolean(a.isExamen === 1 || a.isExamen === true || a.is_examen === 1 || a.is_examen === true)
            );

            if (acts) {
                // If classroom is in exam mode, hide all regular activities
                if (isExamMode) {
                    setStudentActivities([]);
                } else {
                    // Filter out estat 1 (Hidden/No visible)
                    setStudentActivities(acts.filter((a: any) => a.estat !== 1).sort((a: any, b: any) => (a.ordre || 0) - (b.ordre || 0)));
                }
            }
            if (pracs) {
                // If classroom is in exam mode, only show active exams (is_examen === 1 && estat === 3)
                if (isExamMode) {
                    setStudentPractices(pracs.filter((p: any) => (p.is_examen === 1 || p.isExamen === 1) && p.estat === 3).sort((a: any, b: any) => (a.ordre || 0) - (b.ordre || 0)));
                } else {
                    // Filter out estat 1 (Hidden/No visible)
                    setStudentPractices(pracs.filter((p: any) => p.estat !== 1).sort((a: any, b: any) => (a.ordre || 0) - (b.ordre || 0)));
                }
            }

            // Chat stats
            if (interactions && Array.isArray(interactions)) {
                const active = interactions.filter((i: any) => i.estat === 0).length;
                const closed = interactions.filter((i: any) => i.estat !== 0).length;
                setActiveChats(active);
                setClosedChats(closed);

                // Placeholder for participation until backend support
                setParticipation(interactions.length > 0 ? 100 : 0);
            }

        } catch (e) {
            console.error("Error loading student dashboard data", e);
        } finally {
            setLoadingData(false);
        }
    };

    if (isStudent) {
        const currentStudent = processedStudents.find((s: any) => String(s.id) === String(userId));

        const renderItem = (p: any, type: 'activity' | 'practice') => {
            let val = type === 'activity' ? p.progres : p.nota;
            const isNP = val === null || val === undefined;
            // Ensure valid number for calculations, keeping 0 as 0
            const safeVal = typeof val === 'number' ? val : 0;
            const percent = safeVal; // Both are 0-100 based

            const label = type === 'activity' ? 'Progrés' : 'Nota';

            return (
                <Col span={12} key={p.id}>
                    <Card
                        size="small"
                        hoverable
                        onClick={() => {
                            const userAulas = alumne?.aules || [];
                            const isExamMode = (userAulas || []).some((a: any) =>
                                Boolean(a.isExamen === 1 || a.isExamen === true || a.is_examen === 1 || a.is_examen === true)
                            );
                            if (isExamMode) {
                                const isExam = Number(p.is_examen ?? p.isExamen) === 1;
                                if (!isExam || p.estat !== 3) {
                                    return;
                                }
                            }
                            navigate(type === 'activity' ? `/jutge/activitats/${p.id}` : `/jutge/practiques/${p.id}`);
                        }}
                        style={{ cursor: 'pointer', height: '100%' }}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Text strong>{p.nom}</Text>
                            {(p.estat === 2 || p.estat === 1) && <Tag color="red">Tancat</Tag>}
                        </div>
                        <div style={{ marginTop: 10 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                                <span>{label}: {!isNP ? Number(safeVal).toFixed(2) : ''}</span>
                                {isNP ? <Tag color="purple">N/P</Tag> : <span>{Number(safeVal).toFixed(2)}%</span>}
                            </div>
                            {!isNP && (
                                <div style={{ height: 6, background: '#f0f0f0', borderRadius: 3, overflow: 'hidden' }}>
                                    <div style={{ height: '100%', width: `${Math.min(percent, 100)}%`, background: getGradeColor(percent), transition: 'width 0.3s' }} />
                                </div>
                            )}
                        </div>
                    </Card>
                </Col>
            );
        };

        return (
            <Layout style={{ minHeight: '100vh' }}>
                <AppSider currentVal={0} setPantalla={() => { }} />
                <Content style={{ margin: '24px' }}>
                    <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        {aulas.length > 0 && (
                            <Select
                                value={selectedAula}
                                style={{ width: 250 }}
                                onChange={setSelectedAula}
                                loading={loadingData}
                            >
                                {aulas.map(a => <Option key={a.id} value={a.id}>{a.nom}</Option>)}
                            </Select>
                        )}
                        <Button icon={<ReloadOutlined />} onClick={fetchStudentData}>Refrescar</Button>
                    </div>

                    {loadingData ? <div style={{ textAlign: 'center', marginTop: 50 }}><Spin size="large" /></div> : (
                        <Row gutter={[24, 24]}>
                            {/* User Profile & Grades Card */}
                            <Col xs={24} md={8}>
                                <Card title="El meu perfil" bordered={false} style={{ height: '100%' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 20 }}>
                                        <Avatar src={getAvatarSrc(avatars[userId + ""])} size={100} style={{ marginBottom: 10 }}>{currentStudent?.nom?.[0]}</Avatar>
                                        <Title level={4}>{currentStudent?.nom} {currentStudent?.cognoms}</Title>
                                        <Tag color="blue">Estudiant</Tag>
                                    </div>
                                    <Statistic
                                        title="Mitjana Global"
                                        value={currentStudent?.averageGrade || 0}
                                        precision={2}
                                        valueStyle={{ color: getGradeColor(currentStudent?.averageGrade || 0) }}
                                    />
                                    <div style={{ marginTop: 20 }}>
                                        <Text strong>Notes per RA:</Text>
                                        <div style={{ marginTop: 10 }}>
                                            {currentStudent?.raGrades?.map((ra: any) => (
                                                <div key={ra.nom} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                                                    <Text>{ra.nom}</Text>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                                        {ra.isCapped && (
                                                            <Tooltip title="Nota limitada a 4 por no alcanzar el mínimo en un instrumento">
                                                                <span style={{ cursor: 'help', fontSize: 12 }}>⚠️</span>
                                                            </Tooltip>
                                                        )}
                                                        <Tag color={ra.nota !== null ? getGradeColor(ra.nota) : "purple"}>
                                                            {ra.nota !== null ? ra.nota.toFixed(2) : 'N/P'}
                                                        </Tag>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </Card>
                            </Col>

                            <Col xs={24} md={16}>
                                <Card title="Activitat i Xat" bordered={false} style={{ marginBottom: 24 }}>
                                    <Row gutter={16}>
                                        <Col span={8}>
                                            <Statistic title="Converses Actives" value={activeChats} prefix={<UserSwitchOutlined />} valueStyle={{ color: '#1890ff' }} />
                                        </Col>
                                        <Col span={8}>
                                            <Statistic title="Converses Tancades" value={closedChats} />
                                        </Col>
                                        <Col span={8}>
                                            <Statistic title="Participació (%)" value={participation} suffix="%" valueStyle={{ color: '#3f8600' }} />
                                            <div style={{ fontSize: 12, color: '#999' }}>Converses d'altres companys</div>
                                        </Col>
                                    </Row>
                                </Card>

                                <Card title="Activitats" bordered={false} style={{ marginBottom: 24 }}>
                                    {studentActivities.length > 0 ? (
                                        <Row gutter={[16, 16]}>
                                            {studentActivities.map(p => renderItem(p, 'activity'))}
                                        </Row>
                                    ) : <Empty description="Cap activitat" image={Empty.PRESENTED_IMAGE_SIMPLE} />}
                                </Card>

                                <Card title="Pràctiques" bordered={false}>
                                    {studentPractices.length > 0 ? (
                                        <Row gutter={[16, 16]}>
                                            {studentPractices.map(p => renderItem(p, 'practice'))}
                                        </Row>
                                    ) : <Empty description="Cap pràctica" image={Empty.PRESENTED_IMAGE_SIMPLE} />}
                                </Card>
                            </Col>
                        </Row>
                    )}
                </Content>
            </Layout>
        );
    }

    return (
        <>
            <AppSider currentVal={0} setPantalla={() => { }} />
            <Content style={{ padding: '24px', background: '#f5f7fa', minHeight: 'calc(100vh - 64px)' }}>

                <div style={{ marginBottom: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <div>
                        <Title level={2} style={{ margin: 0, color: '#1f1f1f' }}>Dashboard del Aula</Title>
                        <Text type="secondary">Monitorización de progreso y control de entornos</Text>
                    </div>

                    <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                        <Select
                            style={{ width: 240 }}
                            placeholder="Selecciona un Aula"
                            value={selectedAula}
                            onChange={(val) => {
                                setSelectedAula(val);
                                setSelectedItemKey(null);
                            }}
                            loading={aulas.length === 0}
                            size="large"
                        >
                            {aulas.map(a => <Option key={a.id} value={a.id}>{a.nom}</Option>)}
                        </Select>

                        <Select
                            style={{ width: 320 }}
                            placeholder="Seleccionar activitat o pràctica..."
                            allowClear
                            value={selectedItemKey}
                            onChange={(val) => setSelectedItemKey(val)}
                            size="large"
                            disabled={!selectedAula}
                        >
                            <OptGroup label="Activitats">
                                {allActivitats
                                    .filter((act: any) => String(act.id_aula) === String(selectedAula))
                                    .map((act: any) => (
                                        <Option key={`act-${act.id}`} value={`act-${act.id}`}>
                                            📘 {act.nom}
                                        </Option>
                                    ))}
                            </OptGroup>
                            <OptGroup label="Pràctiques">
                                {allPractiques
                                    .filter((p: any) => String(p.id_aula) === String(selectedAula))
                                    .map((p: any) => (
                                        <Option key={`prac-${p.id}`} value={`prac-${p.id}`}>
                                            📝 {p.nom}
                                        </Option>
                                    ))}
                            </OptGroup>
                        </Select>

                        {selectedAula && !selectedItemKey && (
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                <Select
                                    value={viewMode}
                                    onChange={(val) => setViewMode(val)}
                                    size="large"
                                    style={{ width: 220 }}
                                    dropdownMatchSelectWidth={false}
                                >
                                    <Option value="aula">
                                        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                            <DragOutlined style={{ color: '#1890ff' }} /> Vista Aula (Lliure)
                                        </span>
                                    </Option>
                                    <Option value="graella">
                                        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                            <AppstoreOutlined style={{ color: '#722ed1' }} /> Vista Graella
                                        </span>
                                    </Option>
                                    <Option value="joc">
                                        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                            <ControlOutlined style={{ color: '#52c41a' }} /> Vista Joc
                                        </span>
                                    </Option>
                                </Select>

                                {viewMode === 'aula' && (
                                    <Button
                                        icon={<UndoOutlined />}
                                        onClick={handleResetLayout}
                                        size="large"
                                        title="Restablir posicions inicials"
                                    />
                                )}
                            </div>
                        )}

                        <Button
                            icon={<ReloadOutlined />}
                            onClick={() => {
                                fetchSessions();
                                if (selectedAula) {
                                    fetchClassroomStats(selectedAula);
                                    if (viewMode === 'joc') {
                                        fetchClassroomGameData();
                                    }
                                }
                                fetchUserActivitatsAndPractiques();
                            }}
                            size="large"
                        />
                    </div>
                </div>

                {/* Subview 1: Activity selected -> ActivitatsIdEstadistiques */}
                {selectedItemKey && selectedItemKey.startsWith('act-') && (
                    <div style={{ background: '#fff', borderRadius: 12, padding: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                        <ActivitatsIdEstadistiques
                            idActivitatProp={selectedItemKey.replace('act-', '')}
                            onClose={() => setSelectedItemKey(null)}
                        />
                    </div>
                )}

                {/* Subview 2: Practice selected -> PracticaIdAlumnes */}
                {selectedItemKey && selectedItemKey.startsWith('prac-') && (
                    <div style={{ background: '#fff', borderRadius: 12, padding: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                        <PracticaIdAlumnes
                            id_practica={selectedItemKey.replace('prac-', '')}
                            onClose={() => setSelectedItemKey(null)}
                        />
                    </div>
                )}

                {/* Subview 3: Aula Only View (Stats + Free Classroom or Grid Layout) */}
                {!selectedItemKey && (
                    <>
                        {selectedAula && classroomData && (
                            <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
                                {/* Stats Column */}
                                <Col xs={24} md={12} lg={14}>
                                    <Row gutter={[16, 16]}>
                                        {/* Global Stats Cards */}
                                        <Col span={12}>
                                            <Card bordered={false} style={{ borderRadius: 12, height: '100%' }}>
                                                <Statistic
                                                    title="Total Alumnos"
                                                    value={processedStudents.length}
                                                    prefix={<UserOutlined />}
                                                />
                                            </Card>
                                        </Col>
                                        <Col span={12}>
                                            <Card bordered={false} style={{ borderRadius: 12, height: '100%' }}>
                                                <Statistic
                                                    title="IDEs Activos"
                                                    value={globalStats.active}
                                                    valueStyle={{ color: globalStats.active > 0 ? '#52c41a' : '#8c8c8c' }}
                                                    prefix={<DesktopOutlined />}
                                                />
                                                <Text type="secondary" style={{ fontSize: 12 }}>De {processedStudents.length} alumnos</Text>
                                            </Card>
                                        </Col>

                                        {/* RA Averages */}
                                        <Col span={24}>
                                            <Card bordered={false} style={{ borderRadius: 12, marginBottom: 0 }} title="Media por RA (0-100)">
                                                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                                                    {globalStats.raAverages.map((ra: any) => (
                                                        <div key={ra.nom} style={{ flex: 1, minWidth: 60, textAlign: 'center' }}>
                                                            <div style={{ fontSize: 10, color: '#888', marginBottom: 4 }}>RA-{ra.nom}</div>
                                                            <div style={{ height: 60, background: '#f5f5f5', borderRadius: 4, position: 'relative', width: '100%' }}>
                                                                <Tooltip title={`${ra.avg.toFixed(1)}/10`}>
                                                                    <div style={{
                                                                        position: 'absolute', bottom: 0, left: 0, right: 0,
                                                                        height: `${Math.min(ra.avg * 10, 100)}%`,
                                                                        background: getGradeColor(ra.avg),
                                                                        borderRadius: 4,
                                                                        transition: 'all 0.5s'
                                                                    }} />
                                                                </Tooltip>
                                                            </div>
                                                            <div style={{ fontSize: 11, fontWeight: 'bold', marginTop: 4 }}>{ra.avg.toFixed(1)}</div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </Card>
                                        </Col>
                                    </Row>
                                </Col>

                                {/* Stacked Chart (Replaces Donut) */}
                                <Col xs={24} md={12} lg={10}>
                                    <Card
                                        title={<span><BarChartOutlined /> Distribución de Notas por RA</span>}
                                        bordered={false}
                                        style={{ borderRadius: 12, height: '100%', minHeight: 300 }}
                                        bodyStyle={{ padding: 20, height: 320 }}
                                    >
                                        <RaStackedBarChart
                                            students={processedStudents}
                                            ras={globalStats.raAverages.map((r: any) => r.nom)}
                                        />
                                    </Card>
                                </Col>
                            </Row>
                        )}

                        {loadingData ? (
                            <div style={{ textAlign: 'center', padding: 50 }}>
                                <Spin size="large" />
                                <p style={{ marginTop: 10, color: '#999' }}>Cargando datos del aula...</p>
                            </div>
                        ) : !selectedAula ? (
                            <Empty description="Selecciona un aula para ver el dashboard" style={{ marginTop: 100 }} />
                        ) : viewMode === 'aula' ? (
                            /* --- FREE CLASSROOM LAYOUT CANVAS --- */
                            <div style={{
                                background: '#ffffff',
                                border: '1px solid #e8e8e8',
                                borderRadius: 16,
                                padding: '24px 20px',
                                boxShadow: '0 4px 16px rgba(0,0,0,0.04)',
                                marginBottom: 24
                            }}>
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: 16,
                                    paddingBottom: 12,
                                    borderBottom: '1px dashed #d9d9d9'
                                }}>
                                    <div>
                                        <Text strong style={{ fontSize: 16, color: '#262626' }}>🏫 Distribució de l'Aula (Taules)</Text>
                                        <Text type="secondary" style={{ marginLeft: 12, fontSize: 13 }}>
                                            Arrossega les targetes lliurement per ubicar a cada alumne al seu lloc a l'aula
                                        </Text>
                                    </div>
                                    <Tag color="cyan" style={{ borderRadius: 12, padding: '2px 10px' }}>
                                        Arrossegar per moure • Es guarda automàticament
                                    </Tag>
                                </div>

                                {/* Canvas container with relative positioning */}
                                <div
                                    onDragOver={(e) => e.preventDefault()}
                                    onDrop={(e) => {
                                        e.preventDefault();
                                        const cardId = e.dataTransfer.getData("text/plain");
                                        if (!cardId) return;

                                        // Retrieve exact mouse offset relative to the card's top-left corner
                                        const offsetXRaw = e.dataTransfer.getData("application/x-offset-x");
                                        const offsetYRaw = e.dataTransfer.getData("application/x-offset-y");
                                        const offsetX = offsetXRaw ? parseFloat(offsetXRaw) : 0;
                                        const offsetY = offsetYRaw ? parseFloat(offsetYRaw) : 0;

                                        const container = e.currentTarget;
                                        const rect = container.getBoundingClientRect();

                                        // Exact position taking container scroll into account
                                        const cardWidth = cardId === 'teacher' ? 440 : 235;
                                        const rawX = e.clientX - rect.left + container.scrollLeft - offsetX;
                                        const rawY = e.clientY - rect.top + container.scrollTop - offsetY;

                                        const maxX = Math.max(10, (container.scrollWidth || rect.width) - cardWidth - 10);
                                        const maxY = Math.max(10, (container.scrollHeight || 1200) - 150);

                                        const x = Math.max(10, Math.min(maxX, Math.round(rawX)));
                                        const y = Math.max(10, Math.min(maxY, Math.round(rawY)));

                                        const newPositions = { ...cardPositions, [cardId]: { x, y } };
                                        setCardPositions(newPositions);
                                        handleSaveLayout(newPositions);
                                    }}
                                    style={{
                                        position: 'relative',
                                        width: '100%',
                                        minHeight: 900,
                                        background: '#fafbfc',
                                        border: '2px dashed #e6e9ed',
                                        borderRadius: 12,
                                        overflow: 'auto',
                                        backgroundImage: 'radial-gradient(#d3d9e0 1px, transparent 1px)',
                                        backgroundSize: '24px 24px'
                                    }}
                                >
                                    {/* TEACHER DOUBLE DESK CARD */}
                                    {(() => {
                                        const teacherPos = cardPositions['teacher'] || { x: 380, y: 20 };
                                        const teacherUser = user as any;
                                        const teacherAvatar = getAvatarSrc(teacherUser?.avatar || avatars[userId + ""]);
                                        const teacherName = teacherUser?.nom ? `${teacherUser.nom} ${teacherUser.cognoms || ''}` : 'Profesor';

                                        return (
                                            <div
                                                draggable
                                                onDragStart={(e) => {
                                                    e.dataTransfer.setData("text/plain", "teacher");
                                                    const cardRect = e.currentTarget.getBoundingClientRect();
                                                    e.dataTransfer.setData("application/x-offset-x", (e.clientX - cardRect.left).toString());
                                                    e.dataTransfer.setData("application/x-offset-y", (e.clientY - cardRect.top).toString());
                                                }}
                                                style={{
                                                    position: 'absolute',
                                                    left: teacherPos.x,
                                                    top: teacherPos.y,
                                                    width: 440,
                                                    cursor: 'grab',
                                                    zIndex: 20,
                                                    transition: 'box-shadow 0.2s'
                                                }}
                                            >
                                                <Card
                                                    size="small"
                                                    bordered={false}
                                                    style={{
                                                        borderRadius: 14,
                                                        background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                                                        color: '#ffffff',
                                                        boxShadow: '0 6px 18px rgba(24, 144, 255, 0.35)',
                                                        border: '2px solid #69c0ff'
                                                    }}
                                                    bodyStyle={{ padding: '14px 18px' }}
                                                >
                                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                                                            <Avatar
                                                                size={54}
                                                                src={teacherAvatar}
                                                                style={{
                                                                    border: '2px solid #ffffff',
                                                                    backgroundColor: '#003a8c',
                                                                    color: '#fff',
                                                                    fontWeight: 'bold',
                                                                    fontSize: 20
                                                                }}
                                                            >
                                                                {teacherName[0]}
                                                            </Avatar>
                                                            <div>
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                                    <Tag color="#52c41a" style={{ color: '#fff', fontWeight: 'bold', border: 'none', borderRadius: 4 }}>
                                                                        TAULA PROFESSOR
                                                                    </Tag>
                                                                    <Tag color="#fff" style={{ color: '#096dd9', fontWeight: 600, border: 'none', borderRadius: 4 }}>
                                                                        DOCENT
                                                                    </Tag>
                                                                </div>
                                                                <div style={{ fontSize: 16, fontWeight: 'bold', marginTop: 4, color: '#ffffff' }}>
                                                                    {teacherName}
                                                                </div>
                                                                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.85)' }}>
                                                                    {(user as any)?.email || 'Sessió docent activa'}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                                            <DragOutlined style={{ fontSize: 20, color: 'rgba(255,255,255,0.8)' }} />
                                                            <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.7)', marginTop: 4 }}>Moure taula</span>
                                                        </div>
                                                    </div>
                                                </Card>
                                            </div>
                                        );
                                    })()}

                                    {/* STUDENT DESK CARDS */}
                                    {processedStudents.map((student, index) => {
                                        const activeSession = sessions[student.id + ""];
                                        const initials = (student.nom[0] + student.cognoms[0]).toUpperCase();
                                        const avatarColor = stringToColor(student.nom + student.cognoms);

                                        // Default grid placement if no custom coordinate is saved yet
                                        const cols = 4;
                                        const colIndex = index % cols;
                                        const rowIndex = Math.floor(index / cols);
                                        const defaultX = 40 + colIndex * 260;
                                        const defaultY = 160 + rowIndex * 175;

                                        const pos = cardPositions[student.id + ""] || { x: defaultX, y: defaultY };

                                        return (
                                            <div
                                                key={student.id}
                                                draggable
                                                onDragStart={(e) => {
                                                    e.dataTransfer.setData("text/plain", student.id + "");
                                                    const cardRect = e.currentTarget.getBoundingClientRect();
                                                    e.dataTransfer.setData("application/x-offset-x", (e.clientX - cardRect.left).toString());
                                                    e.dataTransfer.setData("application/x-offset-y", (e.clientY - cardRect.top).toString());
                                                }}
                                                style={{
                                                    position: 'absolute',
                                                    left: pos.x,
                                                    top: pos.y,
                                                    width: 235,
                                                    cursor: 'grab',
                                                    zIndex: 10,
                                                    transition: 'box-shadow 0.2s'
                                                }}
                                            >
                                                <Card
                                                    hoverable
                                                    bordered={false}
                                                    style={{
                                                        borderRadius: 12,
                                                        boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
                                                        borderTop: activeSession ? '3px solid #52c41a' : '3px solid #d9d9d9',
                                                        background: '#ffffff'
                                                    }}
                                                    bodyStyle={{ padding: 12 }}
                                                >
                                                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
                                                        <div onClick={() => handleStudentClick(student)} style={{ cursor: 'pointer', flexShrink: 0 }}>
                                                            <Avatar
                                                                size={40}
                                                                src={student.avatar}
                                                                style={{
                                                                    backgroundColor: avatarColor,
                                                                    verticalAlign: 'middle',
                                                                    border: activeSession ? '2px solid #52c41a' : '1px solid #f0f0f0'
                                                                }}
                                                            >
                                                                {!student.avatar && initials}
                                                            </Avatar>
                                                        </div>

                                                        <div style={{ marginLeft: 10, flexGrow: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                                                            <Text
                                                                strong
                                                                onClick={() => handleStudentClick(student)}
                                                                style={{
                                                                    display: 'block',
                                                                    whiteSpace: 'nowrap',
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    fontSize: 13,
                                                                    lineHeight: 1.2,
                                                                    cursor: 'pointer',
                                                                    marginBottom: 2
                                                                }}
                                                                title={`${student.nom} ${student.cognoms}`}
                                                            >
                                                                {student.nom} {student.cognoms}
                                                            </Text>

                                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                {activeSession ? (
                                                                    <Tag color="success" style={{ margin: 0, fontSize: 10, borderRadius: 8, padding: '0 4px' }}>ONLINE</Tag>
                                                                ) : (
                                                                    <Tag color="default" style={{ margin: 0, fontSize: 10, borderRadius: 8, padding: '0 4px', color: '#999' }}>OFFLINE</Tag>
                                                                )}

                                                                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                                                    <Tooltip title="Registro de Sesión y Juego">
                                                                        <ControlOutlined
                                                                            style={{ color: '#52c41a', cursor: 'pointer', fontSize: 13 }}
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                setSelectedGameStudent(student);
                                                                                setGameSessionModalVisible(true);
                                                                            }}
                                                                        />
                                                                    </Tooltip>
                                                                    <Tooltip title="Ver Archivos IDE">
                                                                        <FolderOpenOutlined
                                                                            style={{ color: '#722ed1', cursor: 'pointer', fontSize: 13 }}
                                                                            onClick={(e) => { e.stopPropagation(); openFileBrowser(student); }}
                                                                        />
                                                                    </Tooltip>
                                                                    <Tooltip title="Impersonar Alumno">
                                                                        <UserSwitchOutlined
                                                                            style={{ color: '#1890ff', cursor: 'pointer', fontSize: 13 }}
                                                                            onClick={(e) => { e.stopPropagation(); handleImpersonate(student.id + ""); }}
                                                                        />
                                                                    </Tooltip>
                                                                    <DragOutlined style={{ color: '#bfbfbf', fontSize: 12 }} />
                                                                </div>

                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Numeric RA Grade Indicators */}
                                                    <div style={{ display: 'flex', gap: 2, flexWrap: 'wrap', marginTop: 6 }}>
                                                        {student.raGrades && student.raGrades.length > 0 ? student.raGrades.map((ra: any, idx: number) => {
                                                            const val = ra.nota !== null ? ra.nota : 0;
                                                            const color = ra.nota !== null ? getGradeColor(val) : '#f0f0f0';

                                                            return (
                                                                <Tooltip key={idx} title={`RA-${ra.nom}: ${ra.nota !== null ? ra.nota.toFixed(2) : 'N/P'}`}>
                                                                    <div
                                                                        onClick={() => handleStudentClick(student)}
                                                                        style={{
                                                                            width: 22,
                                                                            height: 14,
                                                                            borderRadius: 2,
                                                                            backgroundColor: color,
                                                                            border: '1px solid #e0e0e0',
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'center',
                                                                            fontSize: '8px',
                                                                            fontWeight: 'bold',
                                                                            color: ra.nota !== null && ra.nota >= 5 ? '#fff' : '#595959',
                                                                            cursor: 'pointer'
                                                                        }}
                                                                    >
                                                                        {ra.nota !== null ? ra.nota.toFixed(1) : '-'}
                                                                    </div>
                                                                </Tooltip>
                                                            );
                                                        }) : (
                                                            <Text type="secondary" style={{ fontSize: 10, width: '100%', textAlign: 'center' }}>Sin datos</Text>
                                                        )}
                                                    </div>

                                                    {/* Bottom info: Student IDE / ID */}
                                                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 6 }}>
                                                        <Tag color="geekblue" style={{ margin: 0, fontSize: 9, lineHeight: '14px', borderRadius: 4, padding: '0 4px' }}>
                                                            IDE: {student.ide !== undefined && student.ide !== null ? student.ide : student.id}
                                                        </Tag>
                                                    </div>
                                                </Card>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ) : viewMode === 'graella' ? (
                            /* --- STANDARD GRID LAYOUT (VISTA GRAELLA) --- */
                            <Row gutter={[16, 16]}>
                                {processedStudents.map(student => {
                                    const activeSession = sessions[student.id + ""];
                                    const initials = (student.nom[0] + student.cognoms[0]).toUpperCase();
                                    const avatarColor = stringToColor(student.nom + student.cognoms);

                                    return (
                                        <Col xs={24} sm={12} md={8} lg={6} xl={4} key={student.id}>
                                            <Card
                                                hoverable
                                                bordered={false}
                                                style={{
                                                    borderRadius: 12,
                                                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                                                    borderTop: activeSession ? '3px solid #52c41a' : '3px solid transparent',
                                                    transition: 'transform 0.2s',
                                                    cursor: 'default'
                                                }}
                                                bodyStyle={{ padding: 12 }}
                                            >
                                                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
                                                    <div onClick={() => handleStudentClick(student)} style={{ cursor: 'pointer', flexShrink: 0 }}>
                                                        <Avatar
                                                            size={42}
                                                            src={student.avatar}
                                                            style={{
                                                                backgroundColor: avatarColor,
                                                                verticalAlign: 'middle',
                                                                border: activeSession ? '2px solid #52c41a' : '1px solid #f0f0f0'
                                                            }}
                                                        >
                                                            {!student.avatar && initials}
                                                        </Avatar>
                                                    </div>

                                                    <div style={{ marginLeft: 10, flexGrow: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                                                        <Text
                                                            strong
                                                            onClick={() => handleStudentClick(student)}
                                                            style={{
                                                                display: 'block',
                                                                whiteSpace: 'nowrap',
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                fontSize: 14,
                                                                lineHeight: 1.2,
                                                                cursor: 'pointer',
                                                                marginBottom: 2
                                                            }}
                                                            title={`${student.nom} ${student.cognoms}`}
                                                        >
                                                            {student.nom} {student.cognoms}
                                                        </Text>

                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                            {activeSession ? (
                                                                <Tag color="success" style={{ margin: 0, fontSize: 10, borderRadius: 8, padding: '0 4px' }}>ONLINE</Tag>
                                                            ) : (
                                                                <Tag color="default" style={{ margin: 0, fontSize: 10, borderRadius: 8, padding: '0 4px', color: '#999' }}>OFFLINE</Tag>
                                                            )}

                                                            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                                                <Tooltip title="Registro de Sesión y Juego">
                                                                    <ControlOutlined
                                                                        style={{ color: '#52c41a', cursor: 'pointer', fontSize: 14 }}
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            setSelectedGameStudent(student);
                                                                            setGameSessionModalVisible(true);
                                                                        }}
                                                                    />
                                                                </Tooltip>

                                                                <Tooltip title="Ver Archivos IDE">
                                                                    <FolderOpenOutlined
                                                                        style={{ color: '#722ed1', cursor: 'pointer', fontSize: 14 }}
                                                                        onClick={(e) => { e.stopPropagation(); openFileBrowser(student); }}
                                                                    />
                                                                </Tooltip>

                                                                <Tooltip title="Impersonar Alumno">
                                                                    <UserSwitchOutlined
                                                                        style={{ color: '#1890ff', cursor: 'pointer', fontSize: 14 }}
                                                                        onClick={(e) => { e.stopPropagation(); handleImpersonate(student.id + ""); }}
                                                                    />
                                                                </Tooltip>
                                                            </div>
                                                        </div>

                                                    </div>
                                                </div>

                                                {/* Numeric RA Grade Indicators */}
                                                <div style={{ display: 'flex', gap: 2, flexWrap: 'wrap', marginTop: 10 }}>
                                                    {student.raGrades && student.raGrades.length > 0 ? student.raGrades.map((ra: any, idx: number) => {
                                                        const val = ra.nota !== null ? ra.nota : 0;
                                                        const color = ra.nota !== null ? getGradeColor(val) : '#f0f0f0';

                                                        return (
                                                            <Tooltip key={idx} title={`RA-${ra.nom}: ${ra.nota !== null ? ra.nota.toFixed(2) : 'N/P'}`}>
                                                                <div
                                                                    onClick={() => handleStudentClick(student)}
                                                                    style={{
                                                                        width: 24,
                                                                        height: 14,
                                                                        borderRadius: 2,
                                                                        backgroundColor: color,
                                                                        border: '1px solid #e0e0e0',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        fontSize: '8px',
                                                                        fontWeight: 'bold',
                                                                        color: ra.nota !== null && ra.nota >= 5 ? '#fff' : '#595959',
                                                                        cursor: 'pointer'
                                                                    }}
                                                                >
                                                                    {ra.nota !== null ? ra.nota.toFixed(1) : '-'}
                                                                </div>
                                                            </Tooltip>
                                                        );
                                                    }) : (
                                                        <Text type="secondary" style={{ fontSize: 10, width: '100%', textAlign: 'center' }}>Sin datos</Text>
                                                    )}
                                                </div>

                                                {/* Bottom info: Student IDE / ID */}
                                                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                                                    <Tag color="geekblue" style={{ margin: 0, fontSize: 9, lineHeight: '14px', borderRadius: 4, padding: '0 4px' }}>
                                                        IDE: {student.ide !== undefined && student.ide !== null ? student.ide : student.id}
                                                    </Tag>
                                                </div>
                                            </Card>
                                        </Col>
                                    );
                                })}
                            </Row>
                        ) : (
                            /* --- VISTA JOC (GRID FORMAT WITH GAME CHARACTER INFO) --- */
                            <Spin spinning={loadingGameData} tip="Cargando información del juego de los alumnos...">
                                <Row gutter={[16, 16]}>
                                    {processedStudents.map(student => {
                                        const activeSession = sessions[student.id + ""];
                                        const initials = (student.nom[0] + student.cognoms[0]).toUpperCase();
                                        const avatarColor = stringToColor(student.nom + student.cognoms);
                                        const gameData = studentsGameData[student.id] || {};
                                        const eq = gameData.equipment || {};
                                        const st = gameData.stats || {};
                                        const ptsTotal = gameData.pointsTotal ?? 0;
                                        const ptsCurrent = gameData.pointsCurrent ?? 0;

                                        // Helper for catalog skin lookup
                                        const findCatalogItem = (type: string, id?: number) => {
                                            if (!catalogSkins || !id) return null;
                                            if (type === 'skin') return catalogSkins.skins?.find((s: any) => s.id === id);
                                            if (type === 'arma') return catalogSkins.armes?.find((a: any) => a.id === id);
                                            if (type === 'escut') return catalogSkins.escuts?.find((e: any) => e.id === id);
                                            if (type === 'arc') return catalogSkins.arcs?.find((a: any) => a.id === id);
                                            if (type === 'flecha') return catalogSkins.flechas?.find((f: any) => f.id === id);
                                            if (type === 'magia') return catalogSkins.magias?.find((m: any) => m.id === id);
                                            return null;
                                        };

                                        const skinItem = findCatalogItem('skin', eq.id_skin);
                                        const armaItem = findCatalogItem('arma', eq.id_arma);
                                        const escutItem = findCatalogItem('escut', eq.id_escut);
                                        const arcItem = findCatalogItem('arc', eq.id_arc);
                                        const flechaItem = findCatalogItem('flecha', eq.id_flecha);
                                        const magiaItem = findCatalogItem('magia', eq.id_magia);

                                        const getItemImg = (item: any, type: string, id?: number) => {
                                            if (item) return getJavamonImage({ ...item, type });
                                            if (id) return `${process.env.REACT_APP_API_URL}:8061/jutge/game/image/${type}/${id}`;
                                            return null;
                                        };

                                        const skinImg = getItemImg(skinItem, 'skin', eq.id_skin);
                                        const armaImg = getItemImg(armaItem, 'arma', eq.id_arma);
                                        const escutImg = getItemImg(escutItem, 'escut', eq.id_escut);
                                        const arcImg = getItemImg(arcItem, 'arc', eq.id_arc);
                                        const flechaImg = getItemImg(flechaItem, 'flecha', eq.id_flecha);
                                        const magiaImg = getItemImg(magiaItem, 'magia', eq.id_magia);

                                        const equipSlots = [
                                            { label: 'Skin', name: skinItem?.nom || st.pj || 'Skin', img: skinImg, icon: <SkinOutlined /> },
                                            { label: 'Arma', name: armaItem?.nom || st.arma || 'Arma', img: armaImg, icon: <ThunderboltOutlined /> },
                                            { label: 'Escut', name: escutItem?.nom || st.escut || 'Escut', img: escutImg, icon: <SafetyCertificateOutlined /> },
                                            { label: 'Arc', name: arcItem?.nom || st.arc || 'Arc', img: arcImg, icon: <AimOutlined /> },
                                            { label: 'Flecha', name: flechaItem?.nom || st.flecha || 'Flecha', img: flechaImg, icon: <FireOutlined /> },
                                            { label: 'Magia', name: magiaItem?.nom || st.magia || 'Magia', img: magiaImg, icon: <CrownOutlined /> }
                                        ];

                                        return (
                                            <Col xs={24} sm={12} md={8} lg={6} xl={4} key={student.id}>
                                                <Card
                                                    hoverable
                                                    bordered={false}
                                                    style={{
                                                        borderRadius: 14,
                                                        boxShadow: '0 4px 14px rgba(0,0,0,0.06)',
                                                        borderTop: activeSession ? '3px solid #52c41a' : '3px solid #722ed1',
                                                        background: 'linear-gradient(180deg, #ffffff 0%, #fafbfc 100%)',
                                                        transition: 'transform 0.2s, box-shadow 0.2s',
                                                        cursor: 'default',
                                                        height: '100%',
                                                        display: 'flex',
                                                        flexDirection: 'column'
                                                    }}
                                                    bodyStyle={{
                                                        padding: '12px 14px',
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        height: '100%'
                                                    }}
                                                >
                                                    {/* Header: Avatar, Name & Online/Offline */}
                                                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
                                                        <div onClick={() => handleStudentClick(student)} style={{ cursor: 'pointer', flexShrink: 0 }}>
                                                            <Avatar
                                                                size={42}
                                                                src={student.avatar}
                                                                style={{
                                                                    backgroundColor: avatarColor,
                                                                    verticalAlign: 'middle',
                                                                    border: activeSession ? '2px solid #52c41a' : '1px solid #d9d9d9'
                                                                }}
                                                            >
                                                                {!student.avatar && initials}
                                                            </Avatar>
                                                        </div>

                                                        <div style={{ marginLeft: 10, flexGrow: 1, minWidth: 0 }}>
                                                            <Text
                                                                strong
                                                                onClick={() => handleStudentClick(student)}
                                                                style={{
                                                                    display: 'block',
                                                                    whiteSpace: 'nowrap',
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    fontSize: 13,
                                                                    lineHeight: 1.2,
                                                                    cursor: 'pointer',
                                                                    marginBottom: 2
                                                                }}
                                                                title={`${student.nom} ${student.cognoms}`}
                                                            >
                                                                {student.nom} {student.cognoms}
                                                            </Text>

                                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                {activeSession ? (
                                                                    <Tag color="success" style={{ margin: 0, fontSize: 10, borderRadius: 8, padding: '0 4px' }}>ONLINE</Tag>
                                                                ) : (
                                                                    <Tag color="default" style={{ margin: 0, fontSize: 10, borderRadius: 8, padding: '0 4px', color: '#999' }}>OFFLINE</Tag>
                                                                )}

                                                                <Tooltip title="Registro de Sesión y Juego">
                                                                    <ControlOutlined
                                                                        style={{ color: '#52c41a', cursor: 'pointer', fontSize: 14 }}
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            setSelectedGameStudent(student);
                                                                            setGameSessionModalVisible(true);
                                                                        }}
                                                                    />
                                                                </Tooltip>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Character points: Totals and Current */}
                                                    <div style={{
                                                        background: '#f0f5ff',
                                                        border: '1px solid #d6e4ff',
                                                        borderRadius: 8,
                                                        padding: '6px 8px',
                                                        marginBottom: 10,
                                                        display: 'flex',
                                                        justifyContent: 'space-around',
                                                        alignItems: 'center'
                                                    }}>
                                                        <div style={{ textAlign: 'center' }}>
                                                            <div style={{ fontSize: 10, color: '#595959', fontWeight: 600, textTransform: 'uppercase' }}>
                                                                P. Totales
                                                            </div>
                                                            <div style={{ fontSize: 13, fontWeight: 'bold', color: '#1d39c4' }}>
                                                                <StarFilled style={{ color: '#faad14', marginRight: 3, fontSize: 11 }} />
                                                                {ptsTotal}
                                                            </div>
                                                        </div>

                                                        <div style={{ width: 1, height: 22, background: '#adc6ff' }} />

                                                        <div style={{ textAlign: 'center' }}>
                                                            <div style={{ fontSize: 10, color: '#595959', fontWeight: 600, textTransform: 'uppercase' }}>
                                                                P. Actuales
                                                            </div>
                                                            <div style={{ fontSize: 13, fontWeight: 'bold', color: '#52c41a' }}>
                                                                <CrownOutlined style={{ color: '#52c41a', marginRight: 3, fontSize: 11 }} />
                                                                {ptsCurrent}
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Game Character Equipment Grid (Skin, Arma, Escudo, Arco, Flecha, Magia) */}
                                                    <div style={{
                                                        display: 'grid',
                                                        gridTemplateColumns: 'repeat(3, 1fr)',
                                                        gap: 6,
                                                        marginBottom: 12,
                                                        background: '#f9f9f9',
                                                        padding: 6,
                                                        borderRadius: 8,
                                                        border: '1px solid #f0f0f0'
                                                    }}>
                                                        {equipSlots.map((slot, idx) => (
                                                            <Tooltip key={idx} title={`${slot.label}: ${slot.name}`}>
                                                                <div style={{
                                                                    aspectRatio: '1',
                                                                    borderRadius: 6,
                                                                    border: '1px solid #e0e0e0',
                                                                    background: '#ffffff',
                                                                    display: 'flex',
                                                                    flexDirection: 'column',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    overflow: 'hidden',
                                                                    position: 'relative',
                                                                    boxShadow: 'inset 0 0 4px rgba(0,0,0,0.03)'
                                                                }}>
                                                                    {slot.img ? (
                                                                        <img
                                                                            src={slot.img}
                                                                            alt={slot.name}
                                                                            style={{ width: '85%', height: '85%', objectFit: 'contain' }}
                                                                            onError={(e) => {
                                                                                (e.target as HTMLElement).style.display = 'none';
                                                                            }}
                                                                        />
                                                                    ) : (
                                                                        <span style={{ color: '#bfbfbf', fontSize: 16 }}>{slot.icon}</span>
                                                                    )}
                                                                    <div style={{
                                                                        position: 'absolute',
                                                                        bottom: 0,
                                                                        left: 0,
                                                                        right: 0,
                                                                        background: 'rgba(0,0,0,0.6)',
                                                                        color: '#fff',
                                                                        fontSize: 8,
                                                                        textAlign: 'center',
                                                                        lineHeight: '12px'
                                                                    }}>
                                                                        {slot.label}
                                                                    </div>
                                                                </div>
                                                            </Tooltip>
                                                        ))}
                                                    </div>

                                                    {/* Action Buttons: 1. RPG Perfil, 2. Inventari + Equipament */}
                                                    <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
                                                        <Button
                                                            size="small"
                                                            icon={<UserOutlined style={{ color: '#722ed1' }} />}
                                                            onClick={() => setRpgProfileModal({ visible: true, student })}
                                                            style={{
                                                                borderRadius: 6,
                                                                fontSize: 11,
                                                                fontWeight: 600,
                                                                color: '#722ed1',
                                                                borderColor: '#d3adf7',
                                                                background: '#f9f0ff'
                                                            }}
                                                            block
                                                        >
                                                            Perfil RPG
                                                        </Button>

                                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                                                            <Button
                                                                size="small"
                                                                icon={<ShoppingOutlined style={{ color: '#1890ff' }} />}
                                                                onClick={() => setUserInventoryModal({ visible: true, student })}
                                                                style={{
                                                                    borderRadius: 6,
                                                                    fontSize: 11,
                                                                    fontWeight: 600,
                                                                    color: '#1890ff',
                                                                    borderColor: '#91d5ff',
                                                                    background: '#e6f7ff',
                                                                    padding: '0 4px',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center'
                                                                }}
                                                            >
                                                                Inventari
                                                            </Button>

                                                            <Button
                                                                size="small"
                                                                loading={loadingEquipmentUser === student.id}
                                                                icon={<SkinOutlined style={{ color: '#fa8c16' }} />}
                                                                onClick={() => handleOpenUserEquipment(student)}
                                                                style={{
                                                                    borderRadius: 6,
                                                                    fontSize: 11,
                                                                    fontWeight: 600,
                                                                    color: '#fa8c16',
                                                                    borderColor: '#ffd591',
                                                                    background: '#fff7e6',
                                                                    padding: '0 4px',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center'
                                                                }}
                                                            >
                                                                Equipament
                                                            </Button>
                                                        </div>
                                                    </div>

                                                    {/* Reset Position (0,0) Button with Confirmation Modal */}
                                                    <div style={{ marginTop: 6 }}>
                                                        <Popconfirm
                                                            title={`¿Restablecer posición de ${student.nom}?`}
                                                            description="El jugador será teletransportado a las coordenadas iniciales (0, 0)."
                                                            okText="Sí, restablecer"
                                                            cancelText="Cancelar"
                                                            okButtonProps={{ danger: true }}
                                                            onConfirm={() => handleResetPosition(student.id, student.nom)}
                                                        >
                                                            <Button
                                                                size="small"
                                                                icon={<CompassOutlined />}
                                                                danger
                                                                style={{
                                                                    borderRadius: 6,
                                                                    fontSize: 11,
                                                                    fontWeight: 500,
                                                                    background: '#fff1f0',
                                                                    borderColor: '#ffa39e'
                                                                }}
                                                                block
                                                            >
                                                                Restablecer Posición (0,0)
                                                            </Button>
                                                        </Popconfirm>
                                                    </div>

                                                    {/* Bottom info: Student IDE / ID */}
                                                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                                                        <Tag color="geekblue" style={{ margin: 0, fontSize: 9, lineHeight: '14px', borderRadius: 4, padding: '0 4px' }}>
                                                            IDE: {student.ide !== undefined && student.ide !== null ? student.ide : student.id}
                                                        </Tag>
                                                    </div>

                                                </Card>
                                            </Col>
                                        );
                                    })}
                                </Row>
                            </Spin>
                        )}


                        <StudentDetailModal
                            visible={modalVisible}
                            student={selectedStudent}
                            classroomData={classroomData}
                            onClose={() => setModalVisible(false)}
                            raConfig={raConfig}
                        />

                        {browserUser && (
                            <IdeFileBrowserModal
                                visible={browserVisible}
                                onClose={() => setBrowserVisible(false)}
                                userId={browserUser.id}
                                studentName={browserUser.name}
                            />
                        )}

                        <GameSessionModal
                            visible={gameSessionModalVisible}
                            onClose={() => setGameSessionModalVisible(false)}
                            student={selectedGameStudent}
                        />

                        {/* MODAL 1: PERFIL RPG */}
                        <Modal
                            title={
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <UserOutlined style={{ color: '#722ed1', fontSize: 18 }} />
                                    <span>Perfil RPG de {rpgProfileModal.student?.nom} {rpgProfileModal.student?.cognoms}</span>
                                    <Tag color="purple">ID: {rpgProfileModal.student?.id}</Tag>
                                </div>
                            }
                            open={rpgProfileModal.visible}
                            onCancel={() => setRpgProfileModal({ visible: false, student: null })}
                            footer={[
                                <Button key="close" type="primary" onClick={() => setRpgProfileModal({ visible: false, student: null })}>
                                    Cerrar
                                </Button>
                            ]}
                            width={1100}
                            destroyOnClose
                        >
                            {rpgProfileModal.student && (
                                <div style={{ maxHeight: '78vh', overflowY: 'auto', paddingRight: 8 }}>
                                    <Perfil user_id_simulado={String(rpgProfileModal.student.id)} rpgOnly={true} />
                                </div>
                            )}
                        </Modal>

                        {/* MODAL GESTIÓ INVENTARI DE L'ALUMNE */}
                        {userInventoryModal.visible && userInventoryModal.student && (
                            <UserInventoryModal
                                visible={userInventoryModal.visible}
                                onClose={(updatedPoints) => {
                                    const sId = userInventoryModal.student?.id;
                                    if (sId && updatedPoints) {
                                        setStudentsGameData(prev => ({
                                            ...prev,
                                            [sId]: {
                                                ...prev[sId],
                                                pointsTotal: updatedPoints.pointsTotal,
                                                pointsCurrent: updatedPoints.pointsCurrent
                                            }
                                        }));
                                    }
                                    setUserInventoryModal({ visible: false, student: null });
                                }}
                                user={userInventoryModal.student}
                                aulaId={selectedAula ? String(selectedAula) : "0"}
                            />
                        )}

                        {/* MODAL EDITAR EQUIPAMENT I SKINS DE L'ALUMNE */}
                        {userEquipmentModal.visible && userEquipmentModal.editingUser && catalogSkins && (
                            <UserEquipmentEditModal
                                visible={userEquipmentModal.visible}
                                onClose={() => setUserEquipmentModal({ visible: false, student: null, editingUser: null })}
                                editingUser={userEquipmentModal.editingUser}
                                skinsConfig={catalogSkins}
                                onSaved={async () => {
                                    const sId = userEquipmentModal.editingUser?.id_usuari || userEquipmentModal.student?.id;
                                    if (sId) {
                                        const updatedEq = await GetUserEquipment(sId);
                                        if (updatedEq) {
                                            setStudentsGameData(prev => ({
                                                ...prev,
                                                [sId]: { ...prev[sId], equipment: updatedEq }
                                            }));
                                        }
                                    }
                                }}
                            />
                        )}
                    </>
                )}

            </Content>
        </>
    );
};

export default Dashboard;
